import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { useEffect } from 'react'
import { useAppState } from '@/src/common/hooks/useAppState'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { GET_CREDIT_CARD_INFORMATION } from '@/src/common/gql/queries/card'
import type { GetCreditCardInformationQuery } from '@api/mesa/graphql'
import { useLogError } from '@/src/common/hooks/useLogError'

type GetCreditCardInformation = GetCreditCardInformationQuery['getCreditCardInformation']
/** @todo rename {@link GetCreditCardInformation.node} in the backend be more specific and less generic */
type CardInformation = GetCreditCardInformation['node']

/**
 * @deprecated just use the {@link GET_CREDIT_CARD_INFORMATION} cache per the apollo api
 * @todo the only thing this is useful for right now is its integration with a persisten {@link AsyncStorage}. we should replace this with [this](https://www.apollographql.com/docs/react/caching/advanced-topics#persisting-the-cache)
 */
export const useCardStore = create(
  persist<{
    cardInformation: CardInformation
    setCardInformation: (cardInformation: CardInformation) => void
  }>(
    (set) => ({
      cardInformation: undefined,
      setCardInformation: (cardInformation: CardInformation) => set({ cardInformation }),
    }),
    {
      name: 'card-information-storage',
      storage: createJSONStorage(() => AsyncStorage),
    },
  ),
)

export function useLegacyCard() {
  const onError = useLogError()
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { setCardInformation, cardInformation: asyncStorageNode } = useCardStore()
  const { isAppVisible } = useAppState()

  const {
    data: nonAsyncStorageData,
    refetch,
    loading,
    networkStatus,
  } = useLoggedInQuery(GET_CREDIT_CARD_INFORMATION, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onError,
    onCompleted(data) {
      setCardInformation(data.getCreditCardInformation.node)
    },
  })

  const data =
    nonAsyncStorageData ||
    (asyncStorageNode
      ? {
          getCreditCardInformation: {
            __typename: 'FindPaymentCardResponse',
            node: asyncStorageNode,
          },
        }
      : undefined)

  useEffect(() => {
    if (isAppVisible) {
      void refetch()
    }
  }, [isAppVisible, refetch])

  return {
    data,
    /** @deprecated - just use {@link data} instead. in fact, it tells you more information, such as whether no there's no data because no fetch has been made yet or because the fetch _was_ made but the server returned nothing */
    cardInformation: data?.getCreditCardInformation.node,
    loading,
    refetch,
    networkStatus,
  } as const
}

export type CardQuery = ReturnType<typeof useLegacyCard>
